/**
 * HTML Document Viewer
 *
 * Renders server-generated HTML (for Excel, TXT, CSV) inside a sandboxed iframe.
 * Sanitizes content with DOMPurify and supports text-search highlighting.
 */

import { useEffect, useRef, useState } from 'react'
import DOMPurify from 'dompurify'
import { Loader2, AlertTriangle } from 'lucide-react'
import { highlightTextInDOM } from '@/lib/text-highlight'
import type { TextHighlight } from '@/stores/preview-store'

interface HtmlDocumentViewerProps {
  htmlUrl: string
  textHighlight?: TextHighlight | null
  zoom: number
}

export function HtmlDocumentViewer({ htmlUrl, textHighlight, zoom }: HtmlDocumentViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function loadHtml() {
      setIsLoading(true)
      setError(null)

      try {
        const response = await fetch(htmlUrl)
        if (!response.ok) throw new Error(`Failed to fetch HTML: ${response.status}`)
        const rawHtml = await response.text()

        if (cancelled) return

        // Sanitize the HTML to prevent XSS
        // WHOLE_DOCUMENT preserves the <html>/<head>/<body> structure from server-generated HTML
        // ADD_TAGS: ['style'] is needed for server-generated table CSS (extractors escape all content)
        const cleanHtml = DOMPurify.sanitize(rawHtml, {
          WHOLE_DOCUMENT: true,
          ADD_TAGS: ['style'],
          ADD_ATTR: ['class', 'style'],
        })

        const iframe = iframeRef.current
        if (!iframe) return

        const iframeDoc = iframe.contentDocument
        if (!iframeDoc) return

        iframeDoc.open()
        iframeDoc.write(cleanHtml)
        iframeDoc.close()

        if (cancelled) return

        // Apply text highlight after rendering
        if (textHighlight?.chunkText && iframeDoc.body) {
          setTimeout(() => {
            if (!cancelled && iframeDoc.body) {
              highlightTextInDOM(
                iframeDoc.body,
                textHighlight.chunkText,
                textHighlight.sectionHeader,
                true
              )
            }
          }, 100)
        }

        setIsLoading(false)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load document')
          setIsLoading(false)
        }
      }
    }

    void loadHtml()

    return () => {
      cancelled = true
    }
  }, [htmlUrl, textHighlight])

  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 z-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading document...</p>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10">
          <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <iframe
        ref={iframeRef}
        sandbox="allow-same-origin"
        title="Document Preview"
        className="w-full h-full border-0"
        style={{
          zoom,
          display: isLoading || error ? 'none' : 'block',
        }}
      />
    </div>
  )
}
